// BottomBar.tsx
import React, { useState } from 'react';
import { Home, ShoppingBag, Newspaper, Menu, X, ShoppingCart, MessageCircle, Send } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { CartButton } from '../../../../ui/elements/CartButton';
import { usePublic } from '../../../../../context/PublicContext';

const BottomBar: React.FC = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
    const [whatsAppMessage, setWhatsAppMessage] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const { settings } = usePublic();

    const closeSidebar = () => {
        setSidebarOpen(false);
    };

    // Ouvre le modal WhatsApp (même comportement que le bouton flottant du Footer)
    const openWhatsAppModal = () => {
        setSidebarOpen(false);
        setShowWhatsAppModal(true);
    };

    // Envoie le message WhatsApp
    const handleSendWhatsApp = () => {
        const number = settings?.whatsapp_number || "2290197737703";
        const phone = number.replace(/\D/g, '');
        const text = encodeURIComponent(
            whatsAppMessage || 'Bonjour, je viens de votre site web. J\'ai besoin d\'assistance.'
        );
        window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
        setShowWhatsAppModal(false);
        setWhatsAppMessage('');
    };

    // Détecte si une route est active
    const isActive = (path: string) => {
        if (path === '/') return location.pathname === '/';
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    return (
        <>
            {/* Barre inférieure fixe (mobile uniquement) */}
            <div className="fixed bottom-0 left-0 right-0 bg-green-800 shadow-lg border-t border-white/20 backdrop-blur-md z-40 md:hidden">
                <div className="flex justify-between items-center py-0.5 px-3">
                    {/* Accueil */}
                    <button
                        onClick={() => navigate('/')}
                        className={`flex flex-col items-center gap-0.5 transition-colors ${
                            isActive('/') ? 'text-accent' : 'text-white/80 hover:text-accent'
                        }`}
                    >
                        <Home size={20} />
                        <span className={`text-[9px] leading-tight ${isActive('/') ? 'font-semibold' : ''}`}>
                            Accueil
                        </span>
                    </button>

                    {/* Boutique */}
                    <button
                        onClick={() => navigate('/products')}
                        className={`flex flex-col items-center gap-0.5 transition-colors ${
                            isActive('/products') ? 'text-accent' : 'text-white/80 hover:text-accent'
                        }`}
                    >
                        <ShoppingBag size={20} />
                        <span className={`text-[9px] leading-tight ${isActive('/products') ? 'font-semibold' : ''}`}>
                            Boutique
                        </span>
                    </button>

                    {/* Blog */}
                    <button
                        onClick={() => navigate('/blog')}
                        className={`flex flex-col items-center gap-0.5 transition-colors ${
                            isActive('/blog') ? 'text-accent' : 'text-white/80 hover:text-accent'
                        }`}
                    >
                        <Newspaper size={20} />
                        <span className={`text-[9px] leading-tight ${isActive('/blog') ? 'font-semibold' : ''}`}>
                            Blog
                        </span>
                    </button>

                    {/* Panier */}
                    <div className="flex flex-col items-center gap-0.5 text-white/80 hover:text-accent transition-colors">
                        <CartButton />
                        <span className="text-[9px] leading-tight">Panier</span>
                    </div>

                    {/* Menu */}
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="flex flex-col items-center gap-0.5 text-white/80 hover:text-accent transition-colors"
                    >
                        <Menu size={20} />
                        <span className="text-[9px] leading-tight">Menu</span>
                    </button>
                </div>
            </div>

            {/* SIDEBAR */}
            <div
                className={`fixed top-0 right-0 h-full w-72 bg-secondary shadow-2xl transform transition-transform duration-500 ease-in-out z-50 md:hidden ${
                    sidebarOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
            >
                <div className="p-4 flex justify-between items-center border-b border-white/20">
                    <span className="text-lg font-bold text-white">Menu</span>
                    <button
                        onClick={closeSidebar}
                        className="p-1.5 bg-accent rounded-full hover:scale-105 transition-transform duration-200"
                    >
                        <X size={16} className="text-primary" />
                    </button>
                </div>

                <nav className="flex flex-col gap-2.5 p-4 text-white">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 space-y-2">
                        {/* À propos */}
                        <div className="border-b border-white/20 pb-1.5">
                            <Link
                                to="/about"
                                onClick={closeSidebar}
                                className={`block py-1.5 text-base transition-colors duration-200 ${
                                    isActive('/about')
                                        ? 'text-accent font-semibold'
                                        : 'font-medium hover:text-accent'
                                }`}
                            >
                                À propos
                            </Link>
                        </div>

                        {/* Contact */}
                        <div className="border-b border-white/20 pb-1.5">
                            <Link
                                to="/contact"
                                onClick={closeSidebar}
                                className={`block py-1.5 text-base transition-colors duration-200 ${
                                    isActive('/contact')
                                        ? 'text-accent font-semibold'
                                        : 'font-medium hover:text-accent'
                                }`}
                            >
                                Contact
                            </Link>
                        </div>

                        {/* Centre d'aide → ouvre WhatsApp */}
                        <div className="border-b border-white/20 pb-1.5">
                            <button
                                onClick={openWhatsAppModal}
                                className="block w-full text-left py-1.5 text-base font-medium hover:text-accent transition-colors duration-200"
                            >
                                Centre d'aide
                            </button>
                        </div>
                    </div>

                    <div className="mt-2">
                        <button
                            onClick={() => {
                                navigate('/products');
                                closeSidebar();
                            }}
                            className="bg-accent text-primary font-bold py-2.5 rounded-full w-full flex items-center justify-center gap-2 hover:scale-105 transition-transform duration-200 text-sm"
                        >
                            <ShoppingCart size={16} /> Commander
                        </button>
                    </div>
                </nav>
            </div>

            {sidebarOpen && (
                <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={closeSidebar} />
            )}

            {/* ================= MODAL WHATSAPP ================= */}
            {showWhatsAppModal && (
                <>
                    <div
                        className="fixed inset-0 bg-black/40 z-[60] md:hidden"
                        onClick={() => setShowWhatsAppModal(false)}
                    />
                    <div className="fixed bottom-24 right-4 z-[70] w-72 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-2 fade-in duration-200">
                        <div className="bg-green-800 text-white px-4 py-2.5 flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <MessageCircle size={18} />
                                <span className="font-semibold text-sm">WhatsApp</span>
                            </div>
                            <button
                                onClick={() => setShowWhatsAppModal(false)}
                                className="text-white/80 hover:text-white transition-colors"
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <div className="p-4">
                            <p className="text-gray-600 text-xs mb-3">Envoyez-nous un message</p>
                            <textarea
                                value={whatsAppMessage}
                                onChange={(e) => setWhatsAppMessage(e.target.value)}
                                rows={3}
                                className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                                placeholder="Bonjour, je souhaite en savoir plus sur vos produits..."
                            />
                            <button
                                onClick={handleSendWhatsApp}
                                className="w-full mt-3 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 rounded-lg transition flex items-center justify-center gap-2 text-sm"
                            >
                                <Send size={14} /> Envoyer
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default BottomBar;